import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CalendarBlank,
  FileText,
  Target,
  WarningCircle,
} from "@phosphor-icons/react";
import { PIPELINE_COLORS } from "@/lib/themeColors";

const PIPELINE_ROUTE = "/app/commerce/pipeline";
const AGENDA_ROUTE = "/app/commerce/agenda";
const QUOTES_ROUTE = "/app/commerce/quotes";

/* Les étapes sont une projection du pipeline existant, sans nouveau statut métier. */
export const PIPELINE_STAGES = [
  { key: "validation", label: "Devis à valider", color: PIPELINE_COLORS.proposal },
  { key: "ready", label: "À envoyer", color: PIPELINE_COLORS.proposal },
  { key: "client", label: "Attente client", color: PIPELINE_COLORS.opportunity },
  { key: "finalization", label: "Finalisation", color: PIPELINE_COLORS.proposal },
  { key: "won", label: "Gagnées", color: PIPELINE_COLORS.won },
  { key: "lost", label: "Perdues", color: PIPELINE_COLORS.lost },
];

function count(value) {
  const result = Number(value ?? 0);
  return Number.isFinite(result) && result >= 0 ? result : 0;
}

function formatNumber(value) {
  return new Intl.NumberFormat("fr-FR").format(count(value));
}

function MetricRow({ label, value, critical = false }) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-3 border-b border-[var(--do-border)] py-3 last:border-b-0">
      <span className="min-w-0 text-sm text-[var(--do-ink-soft)]">{label}</span>
      <strong
        className={"shrink-0 font-mono-stat text-base tabular-nums " + (
          critical && count(value) > 0
            ? "text-[var(--do-danger)]"
            : "text-[var(--do-ink)]"
        )}
      >
        {formatNumber(value)}
      </strong>
    </div>
  );
}

function BusinessCard({ title, icon: Icon, to, children, testId }) {
  return (
    <section
      className="flex min-w-0 flex-col rounded-2xl border bg-[var(--do-bg-solid)] p-4 shadow-sm sm:p-5"
      style={{ borderColor: "var(--do-border)" }}
      data-testid={testId}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[var(--do-accent-soft)] text-[var(--do-accent)]">
            <Icon size={20} weight="duotone" aria-hidden="true" />
          </span>
          <h2 className="font-display text-base font-bold text-[var(--do-ink)] sm:text-lg">
            {title}
          </h2>
        </div>
        <Link
          to={to}
          className="inline-flex min-h-11 shrink-0 items-center gap-1 rounded-lg px-2 text-xs font-bold text-[var(--do-accent)] hover:bg-[var(--do-bg-2)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--do-accent)]"
          aria-label={"Voir " + title.toLowerCase()}
        >
          Voir <ArrowRight size={15} weight="bold" aria-hidden="true" />
        </Link>
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </section>
  );
}

export function DashboardPriorityAlert({ overdueFollowups }) {
  const overdue = count(overdueFollowups);
  if (overdue === 0) return null;

  return (
    <Link
      to={AGENDA_ROUTE}
      className="flex min-h-12 items-center gap-3 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--do-accent)]"
      data-testid="dashboard-priority-alert"
    >
      <WarningCircle size={20} weight="duotone" className="shrink-0 text-[var(--do-danger)]" aria-hidden="true" />
      <span className="min-w-0 flex-1 text-sm font-semibold text-[var(--do-ink)]">
        {formatNumber(overdue)} relance{overdue > 1 ? "s" : ""} en retard — à traiter en priorité
      </span>
      <span className="hidden text-xs font-semibold text-[var(--do-accent)] sm:inline">Examiner les alertes</span>
      <ArrowRight size={17} weight="bold" className="shrink-0 text-[var(--do-accent)]" aria-hidden="true" />
    </Link>
  );
}

/* La même lecture métier sur desktop et mobile ; les données restent celles de l'API existante. */
export default function DashboardBusinessGrid({ dashboard, todayMeetings = 0, agendaItems = null }) {
  const commercial = dashboard?.commercial || {};
  const quotes = dashboard?.quotes || {};
  const pipeline = dashboard?.financialPeriod?.pipeline || commercial.pipeline || {};
  const stages = PIPELINE_STAGES.map((stage) => ({
    ...stage,
    value: count(pipeline?.[stage.key]?.count),
  }));
  const maxPipeline = Math.max(1, ...stages.map((stage) => stage.value));

  return (
    <div className="grid min-w-0 grid-cols-1 gap-3 lg:grid-cols-3 lg:gap-4" data-testid="dashboard-business-grid">
      <BusinessCard title="Pipeline" icon={Target} to={PIPELINE_ROUTE} testId="dashboard-business-pipeline">
        <div className="space-y-3 pt-1">
          {stages.map((stage) => (
            <div key={stage.key} className="min-w-0">
              <div className="mb-1.5 flex items-center justify-between gap-2 text-xs">
                <span className="truncate text-[var(--do-ink-soft)]">{stage.label}</span>
                <strong className="font-mono-stat tabular-nums text-[var(--do-ink)]">{formatNumber(stage.value)}</strong>
              </div>
              <div
                className="h-1.5 overflow-hidden rounded-full bg-[var(--do-bg-2)]"
                role="progressbar"
                aria-label={stage.label}
                aria-valuenow={stage.value}
                aria-valuemin={0}
                aria-valuemax={maxPipeline}
              >
                <div
                  className="h-full rounded-full"
                  style={{ width: String(100 * stage.value / maxPipeline) + "%", backgroundColor: stage.color }}
                />
              </div>
            </div>
          ))}
        </div>
      </BusinessCard>

      <BusinessCard title="Agenda" icon={CalendarBlank} to={AGENDA_ROUTE} testId="dashboard-business-agenda">
        <MetricRow label="RDV aujourd'hui" value={todayMeetings} />
        <MetricRow label="Relances en retard" value={commercial.overdueFollowupsCount} critical />
        <MetricRow label="Affaires à surveiller" value={commercial.closingSoonCount} />
        {Array.isArray(agendaItems) && (
          <div className="mt-2 divide-y divide-[var(--do-border)]" data-testid="dashboard-mobile-agenda-actions">
            {agendaItems.length === 0 ? (
              <p className="py-3 text-sm text-[var(--do-muted)]">Aucune action à réaliser aujourd'hui ou en retard.</p>
            ) : agendaItems.map((row, index) => (
              <Link
                key={String(row.nextActionNoteId || row.id || index) + "-" + index}
                to={`/app/commerce/prospects/${encodeURIComponent(row.prospectId || row.id || "")}`}
                className="flex min-h-11 items-center justify-between gap-3 py-2.5 text-sm text-[var(--do-ink)]"
              >
                <span className="min-w-0">
                  <span className="block truncate font-semibold">{row.nextAction || (row.nextActionType === "meeting" ? "Rendez-vous" : "Action commerciale")}</span>
                  <span className="block truncate text-xs text-[var(--do-muted)]">{row.company || row.prospectName || row.ownerName || "Prospect"}</span>
                </span>
                <ArrowRight size={15} className="shrink-0 text-[var(--do-accent)]" aria-hidden="true" />
              </Link>
            ))}
          </div>
        )}
        <Link to={AGENDA_ROUTE} className="mt-3 inline-flex min-h-11 items-center text-xs font-semibold text-[var(--do-accent)]">
          Voir les actions à venir <ArrowRight size={14} weight="bold" className="ml-1" aria-hidden="true" />
        </Link>
      </BusinessCard>

      <BusinessCard title="Devis" icon={FileText} to={QUOTES_ROUTE} testId="dashboard-business-quotes">
        <MetricRow label="En attente de validation" value={quotes.pendingValidation} />
        <MetricRow label="À envoyer" value={quotes.approvedToSend} />
        <MetricRow label="Envoyés" value={quotes.sent} />
        <MetricRow label="Acceptés" value={quotes.accepted} />
      </BusinessCard>

    </div>
  );
}
